import { useDealer, useDealerLedger, useFixedAccount, useInvestor, useLedger } from '../../api/hooks';
import type { DealType, PartyType } from '../../types';

/** Everything an official message needs about one party — profile plus current position. */
export interface PartyContact {
  partyType: PartyType;
  name: string;
  fatherName: string | null;
  phone: string | null;
  cnic: string | null;
  address: string | null;
  code: string;
  dealType: DealType;
  status: string;
  memberSince: string | null;

  /** Gross capital paid in, before any withdrawal — what the party has handed over in total. */
  totalDeposited: number;
  /** Money taken back out: drawn against capital on a percentage account, against the agreed
   *  return on a fixed one. */
  totalWithdrawn: number;
  /** Fixed accounts only: the whole agreed sum coming back, capital and uplift together. */
  fixedReturnAgreed: number | null;
  /** Capital currently placed — for a fixed account, the total investment handed over. */
  currentPrincipal: number;
  /** Profit earned but not yet paid out — for a fixed account, the agreed profit still to come. */
  outstandingProfit: number;
  /** Profit already settled. */
  paidProfit: number;
  /** Fixed accounts only: the whole agreed sum still to be returned. */
  fixedRemaining: number | null;

  /** False until the record has loaded — the share button is hidden rather than sending blanks. */
  ready: boolean;
}

/**
 * The profile and financial position of whichever party a tab belongs to — the details every
 * official WhatsApp/SMS message quotes. Kept here so each tab needs one line rather than its own
 * copy of the investor-vs-dealer and fixed-vs-percentage branches.
 *
 * React Query dedupes these calls, so several tabs asking for the same party costs one request.
 */
export function usePartyContact(partyType: PartyType, partyId: number, book?: DealType): PartyContact {
  const investor = useInvestor(partyType === 'Investor' ? partyId : 0);
  const dealer = useDealer(partyType === 'Dealer' ? partyId : 0);

  const record = partyType === 'Investor' ? investor.data : dealer.data;
  // A party may hold both books, so the caller says which one it is reporting on. Without that the
  // figures would come from whichever book the record happens to name, regardless of the tab.
  const dealType: DealType = book ?? record?.dealType ?? 'PercentageBased';
  const isFixed = dealType === 'FixedReturn';

  // Only the model that actually applies is fetched — a fixed account has no accrual ledger and a
  // percentage account has no fixed book, so asking for both would be a wasted round trip.
  const fixedAccount = useFixedAccount(partyType, partyId, !!record && isFixed);
  const investorLedger = useLedger(partyType === 'Investor' && !!record && !isFixed ? partyId : 0);
  const dealerLedger = useDealerLedger(partyType === 'Dealer' && !!record && !isFixed ? partyId : 0);

  const ledger = partyType === 'Investor' ? investorLedger.data : dealerLedger.data;

  return {
    partyType,
    name: record?.fullName ?? '',
    fatherName: record?.fatherName ?? null,
    phone: record?.mobile ?? null,
    cnic: record?.cnic ?? null,
    address: record?.address ?? null,
    code:
      partyType === 'Investor'
        ? investor.data?.investorCode ?? ''
        : dealer.data?.dealerCode ?? '',
    dealType,
    status: record?.status ?? '',
    memberSince: record?.createdAt ?? null,

    // A fixed account is never drawn down before maturity, so gross and placed capital are the
    // same figure there; only the percentage models track withdrawals against the principal.
    totalDeposited: isFixed
      ? fixedAccount.data?.totalInvested ?? 0
      : partyType === 'Investor'
        ? investorLedger.data?.totalDeposited ?? 0
        : dealerLedger.data?.totalGiven ?? 0,
    totalWithdrawn: isFixed
      ? fixedAccount.data?.totalWithdrawn ?? 0
      : partyType === 'Investor'
        ? investorLedger.data?.totalWithdrawn ?? 0
        : dealerLedger.data?.totalReturned ?? 0,
    fixedReturnAgreed: isFixed ? fixedAccount.data?.totalReturnAgreed ?? 0 : null,
    currentPrincipal: isFixed ? fixedAccount.data?.totalInvested ?? 0 : ledger?.currentPrincipal ?? 0,
    outstandingProfit: isFixed ? fixedAccount.data?.totalProfit ?? 0 : ledger?.outstandingProfit ?? 0,
    paidProfit: isFixed
      ? fixedAccount.data?.totalWithdrawn ?? 0
      : partyType === 'Investor'
        ? investorLedger.data?.totalPaidProfit ?? 0
        : dealerLedger.data?.totalReceivedProfit ?? 0,
    fixedRemaining: isFixed ? fixedAccount.data?.totalRemaining ?? 0 : null,

    ready: !!record,
  };
}
